/**
 * The operator console, at app.easting.ai/admin.
 *
 * Inside the portal rather than a third surface: one deploy, one auth stack,
 * one design system, and the audience is one to three people who already have
 * this tab open. Rendered only when `whoami` says `superadmin`, and the server
 * refuses every route anyway, so hiding it is courtesy and not the control.
 *
 * Read-mostly by design. Five sections in the order the question comes up, and
 * four things it can change: mint, rotate, revoke, re-poll. Every one of those
 * asks first, because there is no undo for a revoked key at the moment someone
 * is using it.
 */

import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { AdminApi, ApiError, type Api } from "../api";
import type {
  CustomerRow,
  EventDetail,
  JobsView,
  KeyDetail,
  MintedKey,
  ReconcileResult,
  UsageView,
} from "./types";

type Section = "customers" | "keys" | "jobs" | "events" | "usage";

const SECTIONS: { id: Section; label: string; hint: string }[] = [
  { id: "customers", label: "Customers", hint: "Plan, quota, last activity" },
  { id: "keys", label: "Keys", hint: "Every credential and its history" },
  { id: "jobs", label: "Jobs", hint: "Running, stuck, failed" },
  { id: "events", label: "Billing events", hint: "What Stripe told us" },
  { id: "usage", label: "Usage", hint: "Documents by month, key, and model" },
];

export function Admin({ api, onExit }: { api: Api; onExit: () => void }) {
  const admin = useMemo(() => new AdminApi(api), [api]);
  const [section, setSection] = useState<Section>("customers");
  const [error, setError] = useState<string | null>(null);

  return (
    <div class="admin">
      <nav class="admin-nav">
        {SECTIONS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            class={entry.id === section ? "chip chip-on" : "chip"}
            title={entry.hint}
            onClick={() => {
              setError(null);
              setSection(entry.id);
            }}
          >
            {entry.label}
          </button>
        ))}
        <div class="grow" />
        <button type="button" class="ghost" onClick={onExit}>
          Back to documents
        </button>
      </nav>

      {error && (
        <div class="error" role="alert">
          {error}
          <button type="button" class="ghost" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {section === "customers" && (
        <Customers admin={admin} onError={setError} />
      )}
      {section === "keys" && <Keys admin={admin} onError={setError} />}
      {section === "jobs" && <Jobs admin={admin} onError={setError} />}
      {section === "events" && <Events admin={admin} onError={setError} />}
      {section === "usage" && <Usage admin={admin} onError={setError} />}
    </div>
  );
}

/**
 * Load once per section, and hand back a reload for the actions to call.
 *
 * No polling. The console answers questions an operator asks a few times a
 * day, and a page that refetches every ten seconds would spend more Lambda
 * invocations idling than the product does working.
 */
function useSection<T>(
  load: () => Promise<T>,
  onError: (message: string) => void,
) {
  const [data, setData] = useState<T | null>(null);
  const [busy, setBusy] = useState(true);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      setData(await load());
    } catch (failure) {
      onError(describe(failure));
    } finally {
      setBusy(false);
    }
    // `load` is a fresh closure each render; the section decides when to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, busy, reload };
}

function Customers({
  admin,
  onError,
}: {
  admin: AdminApi;
  onError: (message: string) => void;
}) {
  const { data, busy } = useSection(() => admin.customers(), onError);
  if (busy && !data) return <Loading />;
  const rows = data?.customers ?? [];
  if (!rows.length) return <p class="muted">No users yet.</p>;

  return (
    <div class="table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Plan</th>
            <th class="right">Keys</th>
            <th class="right">This month</th>
            <th>Pool</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.user_id} class={row.internal ? "internal" : undefined}>
              <td>
                <div class="mono">{row.email}</div>
                <div class="meta">
                  {row.internal
                    ? "Easting staff"
                    : (row.stripe_customer_id ?? "no Stripe customer")}
                  {!row.registered && " · invite not redeemed"}
                </div>
              </td>
              <td>
                {plan(row)}
                {row.status && <div class="meta">{row.status}</div>}
              </td>
              <td class="right">
                {row.active_keys}
                {row.keys !== row.active_keys && (
                  <span class="meta"> of {row.keys}</span>
                )}
              </td>
              <td class="right">
                {row.documents}
                {row.included_docs != null && (
                  <span class="meta"> / {row.included_docs}</span>
                )}
              </td>
              <td>
                {row.pool_docs == null ? (
                  <span class="meta">n/a</span>
                ) : (
                  `${row.pool_remaining} of ${row.pool_docs} left`
                )}
              </td>
              <td>{when(row.last_activity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function plan(row: CustomerRow): string {
  if (row.plan === "none") return "none";
  if (row.seats && row.seats > 1) return `${row.plan} · ${row.seats} seats`;
  return row.plan;
}

function Keys({
  admin,
  onError,
}: {
  admin: AdminApi;
  onError: (message: string) => void;
}) {
  const { data, busy, reload } = useSection(() => admin.keys(), onError);
  const [minted, setMinted] = useState<MintedKey | null>(null);
  const [minting, setMinting] = useState(false);

  async function act(
    prompt: string,
    action: () => Promise<MintedKey | void>,
  ): Promise<void> {
    // A confirm step on every mutation. Revoking the key a customer's nightly
    // job runs on is not something to do by mis-click.
    if (!window.confirm(prompt)) return;
    try {
      const result = await action();
      if (result) setMinted(result);
      await reload();
    } catch (failure) {
      onError(describe(failure));
    }
  }

  if (busy && !data) return <Loading />;
  const rows = data?.keys ?? [];

  return (
    <>
      {minted && (
        <PlaintextOnce minted={minted} onDone={() => setMinted(null)} />
      )}
      <div class="admin-actions">
        <button type="button" class="primary" onClick={() => setMinting(true)}>
          Mint a key…
        </button>
      </div>
      {minting && (
        <MintForm
          onCancel={() => setMinting(false)}
          onSubmit={async (body) => {
            setMinting(false);
            try {
              setMinted(await admin.mint(body));
              await reload();
            } catch (failure) {
              onError(describe(failure));
            }
          }}
        />
      )}
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Owner</th>
              <th class="right">This month</th>
              <th class="right">Lifetime</th>
              <th>Allowance</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.prefix}
                class={row.revoked_at ? "revoked" : undefined}
              >
                <td>
                  <div class="mono">{row.prefix}</div>
                  <div class="meta">
                    {row.name}
                    {row.mode === "test" && " · sandbox"}
                    {row.revoked_at && ` · revoked ${when(row.revoked_at)}`}
                  </div>
                </td>
                <td>
                  {row.owner_email ?? <span class="meta">unowned</span>}
                  {row.internal && <div class="meta">internal</div>}
                </td>
                <td class="right">{row.documents}</td>
                <td class="right">{row.lifetime_documents}</td>
                <td>{allowance(row)}</td>
                <td class="right">
                  {!row.revoked_at && (
                    <>
                      <button
                        type="button"
                        class="ghost"
                        onClick={() =>
                          act(
                            `Rotate ${row.prefix}? The old key stops working immediately.`,
                            () => admin.rotate(row.prefix),
                          )
                        }
                      >
                        Rotate
                      </button>
                      <button
                        type="button"
                        class="ghost"
                        onClick={() =>
                          act(
                            `Revoke ${row.prefix}? Anything using it starts failing at once.`,
                            () => admin.revoke(row.prefix),
                          )
                        }
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function allowance(row: KeyDetail): string {
  if (row.pool_docs != null) return `${row.pool_docs} pooled, never expires`;
  if (row.included_docs != null)
    return `${row.included_docs} included, ${row.monthly_cap} ceiling`;
  return `${row.monthly_cap} a month`;
}

/** The plaintext exists here and nowhere else, exactly as the claim page has
 *  it. There is no recovery path by design; there is a rotate button. */
function PlaintextOnce({
  minted,
  onDone,
}: {
  minted: MintedKey;
  onDone: () => void;
}) {
  return (
    <div class="banner">
      <strong>{minted.name}</strong> is minted. Copy the key now: it is shown
      once and cannot be recovered.
      <pre class="mono plaintext">{minted.api_key}</pre>
      <button
        type="button"
        class="ghost"
        onClick={() => {
          void navigator.clipboard?.writeText(minted.api_key);
        }}
      >
        Copy
      </button>
      <button type="button" class="ghost" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

function MintForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (body: {
    name: string;
    monthly_cap: number;
    pool_docs?: number;
    owner_email?: string;
  }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [cap, setCap] = useState(200);
  const [pool, setPool] = useState("");
  const [owner, setOwner] = useState("");

  return (
    <form
      class="card admin-form"
      onSubmit={(event) => {
        event.preventDefault();
        const pooled = pool.trim() ? Number(pool) : undefined;
        onSubmit({
          name: name.trim(),
          // A pool key's monthly_cap doubles as its ceiling, the same
          // convention the CLI follows.
          monthly_cap: pooled ?? cap,
          pool_docs: pooled,
          owner_email: owner.trim() || undefined,
        });
      }}
    >
      <label>
        Name
        <input
          required
          value={name}
          placeholder="Acme Surveying"
          onInput={(event) => setName((event.target as HTMLInputElement).value)}
        />
      </label>
      <label>
        Documents a month
        <input
          type="number"
          min={1}
          value={cap}
          onInput={(event) =>
            setCap(Number((event.target as HTMLInputElement).value))
          }
        />
      </label>
      <label title="Makes this a pool key: a non-expiring lifetime allowance instead of a monthly quota.">
        Pool instead
        <input
          type="number"
          min={1}
          value={pool}
          placeholder="blank for monthly"
          onInput={(event) => setPool((event.target as HTMLInputElement).value)}
        />
      </label>
      <label title="Attaches the key to this person, creating the user row if it does not exist. Leave blank for an arrangement key with no owner.">
        Owner email
        <input
          type="email"
          value={owner}
          placeholder="optional"
          onInput={(event) =>
            setOwner((event.target as HTMLInputElement).value)
          }
        />
      </label>
      <div class="admin-actions">
        <button type="submit" class="primary">
          Mint
        </button>
        <button type="button" class="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Jobs({
  admin,
  onError,
}: {
  admin: AdminApi;
  onError: (message: string) => void;
}) {
  const { data, busy, reload } = useSection(() => admin.jobs(), onError);
  const [polling, setPolling] = useState(false);

  async function poll() {
    setPolling(true);
    try {
      const result = await admin.poll();
      await reload();
      window.alert(
        `Settled ${result.batches_settled} batches, recorded ${result.items_recorded} items, ` +
          `re-queued ${result.jobs_requeued} jobs.`,
      );
    } catch (failure) {
      onError(describe(failure));
    } finally {
      setPolling(false);
    }
  }

  if (busy && !data) return <Loading />;
  const view = data as JobsView | null;
  if (!view) return null;

  return (
    <>
      <div class="admin-actions">
        <StaleNote jobs={view.stale_jobs} batches={view.stale_batches} />
        <div class="grow" />
        <button
          type="button"
          class="secondary"
          disabled={polling}
          onClick={poll}
          title="Runs the same poller the five-minute schedule runs. Safe to press twice: each item bills at most once."
        >
          {polling ? "Polling…" : "Poll now"}
        </button>
      </div>

      <h3>Extractions</h3>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Key</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {view.jobs.map((job) => (
              <tr key={job.id} class={job.stale ? "stale" : undefined}>
                <td>
                  <div class="mono">{job.id}</div>
                  <div class="meta">{job.doc_name}</div>
                </td>
                <td>
                  <div class="mono">{job.key_prefix}</div>
                  <div class="meta">{job.owner_email ?? "unowned"}</div>
                </td>
                <td>
                  {job.status}
                  {job.stale && <span class="flag"> stuck</span>}
                  {job.attempts > 1 && (
                    <div class="meta">{job.attempts} attempts</div>
                  )}
                  {job.error && <div class="meta">{job.error}</div>}
                </td>
                <td>{when(job.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Batches</h3>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Batch</th>
              <th>Key</th>
              <th>Status</th>
              <th class="right">Items</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {view.batches.map((batch) => (
              <tr key={batch.id} class={batch.stale ? "stale" : undefined}>
                <td class="mono">{batch.id}</td>
                <td>
                  <div class="mono">{batch.key_prefix}</div>
                  <div class="meta">{batch.owner_email ?? "unowned"}</div>
                </td>
                <td>
                  {batch.status}
                  {batch.stale && <span class="flag"> stuck</span>}
                </td>
                <td class="right">
                  {batch.succeeded}/{batch.item_count}
                  {batch.failed > 0 && (
                    <div class="meta">{batch.failed} failed</div>
                  )}
                </td>
                <td>{when(batch.submitted_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StaleNote({ jobs, batches }: { jobs: number; batches: number }) {
  if (!jobs && !batches) return <span class="muted">Nothing stuck.</span>;
  return (
    <span class="flag">
      {jobs} stuck {jobs === 1 ? "job" : "jobs"}, {batches} stuck{" "}
      {batches === 1 ? "batch" : "batches"}
    </span>
  );
}

function Events({
  admin,
  onError,
}: {
  admin: AdminApi;
  onError: (message: string) => void;
}) {
  const { data, busy } = useSection(() => admin.events(), onError);
  const [reconciled, setReconciled] = useState<ReconcileResult | null>(null);

  async function reconcile(apply: boolean) {
    if (apply && !window.confirm("Post the missing meter events to Stripe?"))
      return;
    try {
      setReconciled(await admin.reconcile(apply));
    } catch (failure) {
      onError(describe(failure));
    }
  }

  if (busy && !data) return <Loading />;
  const events = (data?.events ?? []) as EventDetail[];

  return (
    <>
      <div class="admin-actions">
        <button
          type="button"
          class="secondary"
          onClick={() => reconcile(false)}
        >
          Check for missed overage
        </button>
        {reconciled && (
          <span class="muted">
            {reconciled.keys_checked} keys, {reconciled.overage_found} with
            overage, {reconciled.events_posted} events{" "}
            {reconciled.applied ? "posted" : "missing"}
            {!reconciled.applied && reconciled.events_posted > 0 && (
              <button
                type="button"
                class="ghost"
                onClick={() => reconcile(true)}
              >
                Post them
              </button>
            )}
            {reconciled.skipped.length > 0 && (
              <div class="flag">
                {reconciled.skipped.length} subscribed keys have no subscription
                row and need a human
              </div>
            )}
          </span>
        )}
      </div>
      <p class="muted small">
        An id, a type, and when we recorded it. There is no handled-or-ignored
        column: the row is written before the handler runs, so a status here
        would be a guess.
      </p>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Type</th>
              <th>Recorded</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td class="mono">{event.id}</td>
                <td>{event.type}</td>
                <td>{when(event.processed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!events.length && <p class="muted">Stripe has told us nothing yet.</p>}
    </>
  );
}

function Usage({
  admin,
  onError,
}: {
  admin: AdminApi;
  onError: (message: string) => void;
}) {
  const { data, busy } = useSection(() => admin.usage(), onError);
  if (busy && !data) return <Loading />;
  const view = data as UsageView | null;
  if (!view) return null;

  return (
    <>
      <h3>By month</h3>
      <p class="muted small">
        Our own documents are counted and then subtracted rather than dropped,
        so a month whose only traffic was a staff demo reads as zero revenue and
        not as zero activity.
      </p>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Month</th>
              <th class="right">Documents</th>
              <th class="right">Internal</th>
              <th class="right">Billable</th>
              <th class="right">Tokens in / out</th>
            </tr>
          </thead>
          <tbody>
            {view.months.map((month) => (
              <tr key={month.month}>
                <td class="mono">{month.month}</td>
                <td class="right">{month.documents}</td>
                <td class="right">{month.internal_documents}</td>
                <td class="right">
                  <strong>{month.documents - month.internal_documents}</strong>
                </td>
                <td class="right meta">
                  {thousands(month.input_tokens)} /{" "}
                  {thousands(month.output_tokens)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>This month, by key</h3>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Owner</th>
              <th class="right">Documents</th>
            </tr>
          </thead>
          <tbody>
            {view.keys.map((row) => (
              <tr
                key={row.key_prefix}
                class={row.internal ? "internal" : undefined}
              >
                <td class="mono">{row.key_prefix}</td>
                <td>
                  {row.owner_email ?? <span class="meta">unowned</span>}
                  {row.internal && <span class="meta"> · internal</span>}
                </td>
                <td class="right">{row.documents}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>This month, by model</h3>
      <p class="muted small">
        Routing a class of document to a cheaper model is a pricing lever, and
        this is the only place its effect shows.
      </p>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Model</th>
              <th class="right">Documents</th>
              <th class="right">Tokens in / out</th>
            </tr>
          </thead>
          <tbody>
            {view.models.map((row) => (
              <tr key={row.model}>
                <td class="mono">{row.model}</td>
                <td class="right">{row.documents}</td>
                <td class="right meta">
                  {thousands(row.input_tokens)} / {thousands(row.output_tokens)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Loading() {
  return <p class="muted">Reading the ledger…</p>;
}

function when(stamp?: string | null): string {
  if (!stamp) return "never";
  const then = new Date(stamp);
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  if (days === 0) return then.toLocaleTimeString();
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return then.toLocaleDateString();
}

function thousands(value: number): string {
  return value.toLocaleString();
}

function describe(failure: unknown): string {
  if (failure instanceof ApiError) return failure.message;
  if (failure instanceof Error) return failure.message;
  return "Something went wrong.";
}
